import React, { useEffect, useMemo } from 'react';
import '../css/animate.css';
import '../css/barvy.css';
import '../css/style.css';
import {motion} from "framer-motion";
import { NavLink } from 'react-router-dom';
import nukeproofImage from '../images/nukeproof.png';
import { getLocalizedText } from '../utils/textLocalization';
import {
  EditableContent,
  EditableNews,
  useEditry,
  usePreloadPublicData,
  usePublicContentList,
  usePublishedNews,
} from '@lazylabs-dev/editry-react';
import { isEditryConfigured } from '../utils/editryConfig';
import { getCurrentLanguage } from '../utils/languageSwitcher';

const LOCALE_TO_CONTENT_LANGUAGE = {
  'cs-CZ': 'cs',
  'sk-SK': 'sk',
  'pl-PL': 'pl',
  'hu-HU': 'hu',
};

const getPreferredLanguages = (locale) => {
  const preferred = LOCALE_TO_CONTENT_LANGUAGE[locale] || 'cs';
  return preferred === 'cs' ? ['cs', 'default'] : [preferred, 'cs', 'default'];
};

const getEditryLanguage = (locale) => LOCALE_TO_CONTENT_LANGUAGE[locale] || 'cs';

const normalizeContentLanguage = (language) => {
  if (typeof language !== 'string') {
    return 'default';
  }

  const normalized = language.trim().toLowerCase();
  return normalized || 'default';
};

const getContentValue = (contentList, contentKey, fallback = '', preferredLanguages = ['cs']) => {
  if (!Array.isArray(contentList)) {
    return fallback;
  }

  const records = contentList.filter((item) => item.content_key === contentKey);
  if (!records.length) {
    return fallback;
  }

  let record = null;
  for (const language of preferredLanguages) {
    const targetLanguage = normalizeContentLanguage(language);
    record = records.find((item) => normalizeContentLanguage(item.language) === targetLanguage);
    if (record) {
      break;
    }
  }

  if (!record) {
    record = records[0];
  }

  return typeof record?.value === 'string' && record.value.trim() !== '' ? record.value : fallback;
};

const toPlainText = (html = '') => html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const truncate = (text = '', max = 100) => {
  if (text.length <= max) {
    return text;
  }

  return `${text.substring(0, max)}...`;
};

function App() {
    const editryReady = isEditryConfigured();
    const locale = getCurrentLanguage();
    const preferredLanguages = useMemo(() => getPreferredLanguages(locale), [locale]);
    const editryLanguage = useMemo(() => getEditryLanguage(locale), [locale]);
    const { overlayEnabled, adminSession } = useEditry();
    const isEditModeActive = overlayEnabled && Boolean(adminSession?.token);

    usePreloadPublicData({ includeNews: true, auto: editryReady });
    const contentListResponse = usePublicContentList();
    const publishedNewsResponse = usePublishedNews();

    const allContent = useMemo(
      () => contentListResponse.data?.data || [],
      [contentListResponse.data]
    );

    // Get localized text
    const homeText = getLocalizedText('general.home_caps');
    const articlesText = getLocalizedText('general.articles');
    const showButtonText = getLocalizedText('general.show_button');

    const headingTitle = getContentValue(allContent, 'home_kola_homedata_novinkytitle', articlesText, preferredLanguages);
    const headingDescription = getContentValue(allContent, 'home_kola_homedata_novinkydesc', '', preferredLanguages);

    const novinkyContent = useMemo(
      () => (publishedNewsResponse.data?.data || [])
        .filter((item) => (item.title || '').toLowerCase() !== 'ignore'),
      [publishedNewsResponse.data]
    );

    useEffect(() => {
        document.title = getLocalizedText('general.novinky_page_title');
      }, []);

    if (editryReady && (contentListResponse.loading || publishedNewsResponse.loading) && !novinkyContent.length) {
        return <div></div>;
      }

    const renderEditableOrText = (contentKey, fallback) => {
      if (!isEditModeActive) {
        return <span>{fallback}</span>;
      }

      return (
        <EditableContent
          contentKey={contentKey}
          language={editryLanguage}
          as="span"
          fallback={fallback}
          showFallbackWhileLoading={false}
        />
      );
    };

  return (
    <>
    <motion.div  key="loading"initial={{ opacity: 0 }}animate={{ opacity: 1 }}exit={{ opacity: 0 }}transition={{ duration: 0.3 }}>

    <div className="cesta-novinky" style={{top: "60px"}}>
          <NavLink to="/"><button><span>{homeText}</span></button></NavLink>
          <button className="cesta-active"><span>{articlesText}</span></button>
    </div>

    <section className="text-center scrollto clearfix" style={{marginTop: "60px"}}>
        <div className="row clearfix">

            <div className="section-heading">
                <h3 className={`barlow-semibold ${!headingTitle && 'loading-text'}`}>
                  {renderEditableOrText('home_kola_homedata_novinkytitle', headingTitle || articlesText)}
                </h3>
                <h2 className={`section-title white-title ${!headingDescription && 'loading-text'}`}>
                  {renderEditableOrText('home_kola_homedata_novinkydesc', headingDescription || 'loading...')}
                </h2>
            </div>
            <div className="novinky-container">
                {novinkyContent
                    .slice(0, 999)
                    .map((novinka, index) => {
                      const card = (
                        <NavLink to={`/article/${novinka.id}`} key={novinka.id || index}>
                            <div className="novinky-box">
                                <div>
                                  <img src={novinka.image_url ? `${novinka.image_url}?format=webp` : nukeproofImage} alt="Nukeproof novinky"/>
                                  <span>{articlesText}</span>
                                </div>
                                <button><span>{showButtonText}</span></button>
                                <h2>{novinka.title || 'Článek'}</h2>
                                <p>{truncate(novinka.excerpt || toPlainText(novinka.content || ''), 100)}</p>
                            </div>
                        </NavLink>
                      );

                      if (!isEditModeActive) {
                        return card;
                      }

                      return (
                        <EditableNews key={novinka.id || index} news={novinka}>
                          {card}
                        </EditableNews>
                      );
                    })}
            </div>
        </div>
    </section>

    </motion.div>
    </>
  );
}

export default App;